import { rgbToHsl, hslToRgb, rgbToHex } from './colors';
import type { RGB } from '../types';

export interface PaletteTheme {
  id: string;
  name: string;
  colors: string[];
}

export interface ThemeCollection {
  id: string;
  name: string;
  description: string;
  palettes: PaletteTheme[];
}

export const generateThemedPalettes = (color: RGB): ThemeCollection[] => {
  const hsl = rgbToHsl(color.r, color.g, color.b);

  const generateColor = (h: number, s: number, l: number) => {
    const rgb = hslToRgb((h + 360) % 360, Math.max(0, Math.min(100, s)), Math.max(0, Math.min(100, l)));
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  };

  // Helper to create a palette
  const createPalette = (name: string, colors: string[]) => ({
    id: name.toLowerCase().replace(/\s+/g, '-'),
    name,
    colors
  });

  // --- Theme Generators ---

  // 1. Pastel
  const pastelPalettes: PaletteTheme[] = [
    createPalette('Soft Dream', [
      generateColor(hsl.h, 50, 90),
      generateColor(hsl.h + 30, 50, 85),
      generateColor(hsl.h + 180, 40, 90),
      generateColor(hsl.h + 210, 40, 85)
    ]),
    createPalette('Cotton Candy', [
      generateColor(hsl.h, 60, 88),
      generateColor(hsl.h + 120, 50, 90),
      generateColor(hsl.h + 240, 50, 88),
      '#FFFFFF'
    ]),
    createPalette('Muted Pastel', [
      generateColor(hsl.h, 30, 80),
      generateColor(hsl.h + 40, 30, 75),
      generateColor(hsl.h - 40, 30, 80),
      generateColor(hsl.h, 10, 90)
    ])
  ];

  // 2. Vintage
  const vintagePalettes: PaletteTheme[] = [
    createPalette('Retro Vibe', [
      generateColor(hsl.h, 40, 60),
      generateColor(hsl.h + 30, 30, 70),
      generateColor(hsl.h + 15, 20, 80),
      generateColor(hsl.h - 30, 40, 50)
    ]),
    createPalette('Nostalgia', [
      generateColor(hsl.h, 30, 50),
      generateColor(hsl.h + 180, 20, 60),
      generateColor(40, 40, 85), // Cream
      generateColor(30, 30, 40)  // Brownish
    ]),
    createPalette('Faded Film', [
      generateColor(hsl.h, 20, 60),
      generateColor(hsl.h + 60, 20, 65),
      generateColor(hsl.h + 120, 15, 55),
      generateColor(hsl.h, 10, 85)
    ])
  ];

  // 3. Neon
  const neonPalettes: PaletteTheme[] = [
    createPalette('Cyberpunk', [
      generateColor(hsl.h, 100, 60),
      generateColor(hsl.h + 180, 100, 60),
      generateColor(hsl.h + 90, 100, 60),
      '#121212'
    ]),
    createPalette('Electric Night', [
      generateColor(hsl.h, 100, 50),
      generateColor(hsl.h - 30, 100, 50),
      '#000000',
      generateColor(hsl.h + 30, 100, 60)
    ]),
    createPalette('High Voltage', [
      generateColor(hsl.h + 180, 100, 50),
      generateColor(hsl.h, 100, 50),
      '#1A1A1A',
      '#FFFFFF'
    ])
  ];

  // 4. Dark
  const darkPalettes: PaletteTheme[] = [
    createPalette('Midnight', [
      generateColor(hsl.h, 30, 20),
      generateColor(hsl.h, 20, 15),
      generateColor(hsl.h + 180, 30, 25),
      generateColor(hsl.h, 10, 10)
    ]),
    createPalette('Deep Space', [
      generateColor(hsl.h, 50, 10),
      generateColor(hsl.h + 60, 40, 15),
      generateColor(hsl.h + 120, 30, 20),
      '#050505'
    ]),
    createPalette('Elegant Dark', [
      generateColor(hsl.h, 20, 15),
      generateColor(hsl.h, 10, 30),
      generateColor(hsl.h, 5, 50),
      generateColor(hsl.h, 40, 10)
    ])
  ];

  // 5. Nature (Warm/Cold/Seasons combined)
  const naturePalettes: PaletteTheme[] = [
    createPalette('Forest Walk', [
      generateColor(120, 30, 30),
      generateColor(100, 40, 40),
      generateColor(40, 50, 80),
      generateColor(30, 40, 30)
    ]),
    createPalette('Ocean Breeze', [
      generateColor(210, 60, 70),
      generateColor(200, 50, 80),
      generateColor(190, 70, 60),
      generateColor(210, 30, 90)
    ]),
    createPalette('Sunset Glow', [
      generateColor(340, 60, 60),
      generateColor(20, 70, 60),
      generateColor(40, 80, 70),
      generateColor(280, 40, 30)
    ])
  ];

  return [
    {
      id: 'pastel',
      name: 'Pastel',
      description: 'Soft, soothing, and desaturated tones.',
      palettes: pastelPalettes
    },
    {
      id: 'vintage',
      name: 'Vintage',
      description: 'Nostalgic, muted, and retro-inspired.',
      palettes: vintagePalettes
    },
    {
      id: 'neon',
      name: 'Neon',
      description: 'Bright, high-saturation, and energetic.',
      palettes: neonPalettes
    },
    {
      id: 'dark',
      name: 'Dark Mode',
      description: 'Deep, moody, and sophisticated.',
      palettes: darkPalettes
    },
    {
      id: 'nature',
      name: 'Nature',
      description: 'Inspired by the natural world.',
      palettes: naturePalettes
    }
  ];
};

